/* The language context: EN or AR, <html lang dir>, and t(). The choice is remembered in localStorage (pa_lang). */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Lang } from '@pathabroad/core/browser';
import { initialLang, slots, translate, type MessageKey, type Vars } from '../lib/i18n';
import { dirOf } from '../lib/format';
import { readLang, writeLang } from '../lib/storage';

type Ctx = {
  lang: Lang;
  /* remember: store the choice (the switch does; following the account's language on sign-in doesn't need to) */
  setLang: (lang: Lang, opts?: { remember?: boolean }) => void;
  t: (key: MessageKey, vars?: Vars) => string;
  /* a message with React elements in its {slots} */
  rich: (key: MessageKey, parts: Record<string, ReactNode>) => ReactNode;
};

const LangContext = createContext<Ctx | null>(null);

export function applyLang(lang: Lang) {
  document.documentElement.lang = lang;
  document.documentElement.dir = dirOf(lang);
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setState] = useState<Lang>(() => initialLang(readLang(), navigator.languages ?? [navigator.language]));
  useEffect(() => { applyLang(lang); }, [lang]);
  const setLang = useCallback((l: Lang, opts: { remember?: boolean } = {}) => {
    if (opts.remember !== false) writeLang(l);
    applyLang(l);
    setState(l);
  }, []);
  const value = useMemo<Ctx>(() => ({
    lang,
    setLang,
    t: (key, vars) => translate(lang, key, vars),
    rich: (key, parts) => slots(translate(lang, key)).map((p, i) =>
      'text' in p ? p.text : <span key={i}>{parts[p.slot] ?? `{${p.slot}}`}</span>),
  }), [lang, setLang]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useT(): Ctx {
  const c = useContext(LangContext);
  if (!c) throw new Error('useT outside LangProvider');
  return c;
}
